import { CommonModule, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
 InjectionToken,
 NgModule,
 PLATFORM_ID,
 TransferState,
 makeStateKey,
 inject,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, UrlSerializer } from '@angular/router';
import { GlobalHttpErrorHandler } from '@core/interceptors/global-http-error-handler.interceptor';
import { LoadingInterceptor } from '@core/interceptors/loading.interceptor';
import { MetaScriptInterceptor } from '@core/interceptors/meta-script.interceptor';
import { UtmInterceptor } from '@core/interceptors/utm.interceptor';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { NotificationComponent } from '@shared/components/notification/notification.component';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { LoginRegisterUiService } from './pages/login-register/login-register-ui.service';
import { CustomUrlSerializerService } from './custom-url-serializer';
import { TokenInterceptor } from '@core/interceptors/token.interceptor';
import { RialToTomanPipe } from '@shared/pipes/rial-to-toman.pipe';
import { SSR_API_BASE_URL } from './core/ssr/ssr-request-context';
export const WINDOW = new InjectionToken<Window>('Window', {
 providedIn: 'root',
 factory: () => window,
});

export const API_IHO_URL = new InjectionToken<string>('API_IHO_URL');
export const SITE_IHO_URL = new InjectionToken<string>('SITE_IHO_URL');
export const SERVER_COOKIES = new InjectionToken<string>('SERVER_COOKIES');
export const EXPRESS_HOST = new InjectionToken<string>('EXPRESS_HOST');
export const cdnUrl = process.env['IHO_CDN_URL'] || 'https://cdn.iranhotelonline.com';

const FEATURE_SITE_URL = 'https://features_prd-1139.ihogroup.ir';
const PRODUCTION_SITE_URL = 'https://www.iranhotelonline.com';

function isFeatureHost(hostname: string): boolean {
 return /^features[_-]/i.test(hostname.replace(/^www\./i, ''));
}

export function getSiteUrl(platformId: Object): string {
 if (process.env['NODE_ENV'] === 'development') return FEATURE_SITE_URL;

 if (isPlatformServer(platformId)) {
  const expressHost = inject(EXPRESS_HOST, { optional: true });
  const hostname = expressHost?.split('/')[0] ?? '';
  return isFeatureHost(hostname) ? `https://${hostname}` : PRODUCTION_SITE_URL;
 }

 if (isPlatformBrowser(platformId) && isFeatureHost(window.location.hostname)) {
  return window.location.origin;
 }

 return PRODUCTION_SITE_URL;
}

export function getBaseUrl(platformId: Object): string {
    if (isPlatformServer(platformId)) {
        const internalApiBase = inject(SSR_API_BASE_URL, { optional: true });
        if (internalApiBase) return internalApiBase;
    }
    if (process.env['NODE_ENV'] == 'development') return 'https://features_PRD-590.ihogroup.ir/api/mvc/';
    else {
        const transferState = inject(TransferState);
        let expressHost = inject(EXPRESS_HOST, { optional: true });
        let baseUrl = '';
        const baseUrlKey = makeStateKey<string>('baseUrlKey');
        if (isPlatformServer(platformId)) {
            baseUrl = 'http://' + (expressHost ?? 'www.iranhotelonline.com/api/mvc/');
            transferState.set(baseUrlKey, expressHost);
        } else {
            baseUrl = '/api/mvc/';
        }
        return baseUrl ?? 'https://www.iranhotelonline.com/api/mvc/';
    }
}

@NgModule({
 declarations: [AppComponent, NotificationComponent],
 imports: [
  BrowserModule,
  BrowserAnimationsModule,
  TransferHttpCacheModule,
  RouterModule.forRoot(appRoutes, {
   initialNavigation: 'enabledBlocking',
   onSameUrlNavigation: 'reload',
  }),
  MatDialogModule,
  HttpClientModule,
  CommonModule,
 ],
 providers: [
  LoginRegisterUiService,
  provideClientHydration(),
  { provide: UrlSerializer, useClass: CustomUrlSerializerService },
  {
   provide: API_IHO_URL,
   useFactory: getBaseUrl,
   deps: [PLATFORM_ID],
  },
  {
   provide: SITE_IHO_URL,
   useFactory: getSiteUrl,
   deps: [PLATFORM_ID],
  },
  {
   provide: WINDOW,
   useFactory: (platformId: Object) => {
    return isPlatformBrowser(platformId) ? window : {};
   },
   deps: [PLATFORM_ID],
  },
  //   { provide: ErrorHandler, useClass: GlobalErrorHandler },
  {
   provide: HTTP_INTERCEPTORS,
   useClass: TokenInterceptor,
   multi: true,
  },
  {
   provide: HTTP_INTERCEPTORS,
   useClass: GlobalHttpErrorHandler,
   multi: true, // because we could have more than one interceptors in our app
  },
  {
   provide: HTTP_INTERCEPTORS,
   useClass: MetaScriptInterceptor,
   multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: UtmInterceptor, multi: true },
  {
   provide: HTTP_INTERCEPTORS,
   useClass: LoadingInterceptor,
   multi: true,
  },
  RialToTomanPipe,
  //   CookieService,
 ],
 bootstrap: [AppComponent],
})
export class AppModule {}
